import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ChevronDown, ChevronRight, ExternalLink, FileText, RefreshCw, X } from "lucide-react";

import type { ReportKey } from "../../hooks/useReportLists";
import { reportName } from "../../utils/reportName";

type Report = Record<string, unknown>;
interface Props {
  open: boolean;
  user: unknown;
  loading: boolean;
  reports: Record<string, Report[]>;
  onClose: () => void;
  onRefresh: () => void;
  onLogin: () => void;
}
interface Section {
  key: ReportKey;
  label: string;
  idKey: string;
  to: (id: string) => string;
  // Shown when a report has no usable name/query/url. Required so every report type,
  // including ones added later, gets a readable label instead of a blank row.
  fallbackName: string;
}
const sectionData: Section[] = [
  {
    key: "audits",
    label: "Audit Reports",
    idKey: "audit_id",
    to: (id) => `/audit/report/${id}`,
    fallbackName: "Audit Report",
  },
  {
    key: "reputation",
    label: "AI Reputation Reports",
    idKey: "report_id",
    to: (id) => `/ai-reputation/report/${id}`,
    fallbackName: "AI Reputation Report",
  },
  {
    key: "visibility",
    label: "Legacy Visibility Reports",
    idKey: "visibility_report_id",
    to: (id) => `/llm-visibility/report/${id}`,
    fallbackName: "Visibility Report",
  },
  {
    key: "ota",
    label: "OTA vs Direct Reports",
    idKey: "ota_report_id",
    to: (id) => `/ota-direct/report/${id}`,
    fallbackName: "OTA vs Direct Report",
  },
  {
    key: "persona",
    label: "Persona Visibility Reports",
    idKey: "report_id",
    to: (id) => `/persona/report/${id}`,
    fallbackName: "Persona Visibility Report",
  },
  {
    key: "perception",
    label: "AI Perception Reports",
    idKey: "report_id",
    to: (id) => `/perception/report/${id}`,
    fallbackName: "AI Perception Report",
  },
];
export const ReportsSidebar = ({
  open,
  user,
  loading,
  reports,
  onClose,
  onRefresh,
  onLogin,
}: Props) => {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    audits: true,
    reputation: true,
  });
  // Derived from sectionData so it matches what is rendered; keys without a section are ignored.
  const hasReports = sectionData.some(({ key }) => reports[key]?.length > 0);
  return (
    <div
      className={`fixed top-0 left-0 h-full w-80 bg-surface-container-lowest shadow-cloud z-[70] transform transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div className="p-6 border-b border-surface-container-high flex items-center justify-between">
        <h3 className="text-lg font-bold">Your Reports</h3>
        <div className="flex gap-2">
          <button aria-label="Refresh reports" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4" />
          </button>
          <button aria-label="Close reports" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="overflow-y-auto h-[calc(100%-73px)]">
        {!user ? (
          <div className="p-6 text-center">
            <FileText className="mx-auto mb-4" />
            <p>Log in to view your saved reports</p>
            <button
              className="mt-4 rounded-xl bg-primary px-4 py-2 text-on-primary"
              onClick={onLogin}
            >
              Log In
            </button>
          </div>
        ) : loading ? (
          <div className="p-6">
            <p className="text-center text-sm text-on-surface-variant">Loading reports...</p>
          </div>
        ) : !hasReports ? (
          <p className="p-6 text-center">No reports yet. Run an audit to get started!</p>
        ) : (
          sectionData.map(({ key, label, idKey, to, fallbackName }) =>
            reports[key]?.length ? (
              <section key={key}>
                <button
                  className="flex w-full items-center gap-2 px-4 pt-3 pb-1"
                  onClick={() => setOpenSections({ ...openSections, [key]: !openSections[key] })}
                >
                  {openSections[key] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  <span className="text-xs font-bold uppercase">{label}</span>
                </button>
                {openSections[key] && (
                  <div className="p-3">
                    {reports[key].map((report) => (
                      <button
                        key={String(report[idKey])}
                        className="mb-1 flex w-full items-center justify-between rounded-xl p-3 text-left hover:bg-surface-container-high"
                        onClick={() => {
                          onClose();
                          navigate(to(String(report[idKey])));
                        }}
                      >
                        <span className="truncate text-sm">{reportName(report, fallbackName)}</span>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                )}
              </section>
            ) : null,
          )
        )}
      </div>
    </div>
  );
};
